import { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Handshake, Send, Star } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui";
import { MetricRow } from "@/components/marketplace";
import { formatCompact, formatPercent } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { CreatorProfile, SocialAccount } from "@/hooks/useMarketplace";
import { PLATFORM_LABELS, countryLabel } from "./constants";
import { ProvenanceBadge, displayHandle, rateLabel } from "./CreatorPresentation";
import InviteToCampaignDialog from "./components/InviteToCampaignDialog";

/**
 * The account a brand judges the creator on: the largest one, preferring an
 * OAuth-verified account when reach is comparable. Ranking an unverified 200k
 * above a verified 180k would reward the number we trust least.
 */
function primaryAccount(accounts: SocialAccount[]): SocialAccount | null {
  if (accounts.length === 0) return null;
  return [...accounts].sort((a, b) => {
    const af = a.followers ?? 0;
    const bf = b.followers ?? 0;
    if (a.verified !== b.verified && Math.abs(af - bf) / Math.max(af, bf, 1) < 0.25) {
      return a.verified ? -1 : 1;
    }
    return bf - af;
  })[0];
}

/**
 * The card IS the photograph.
 *
 * A 4:5 portrait at full bleed with an ink scrim carrying everything a brand
 * needs to shortlist: identity, niches, per-account reach with its provenance,
 * and the rate. The previous card was a white box with a 44px avatar, which
 * made thirty creators look like thirty rows of a spreadsheet — in a market
 * where the whole product is people, that is the wrong unit.
 *
 * Where there is no photograph the system renders diagonal stripes, never a
 * grey box and never a stock photo. An obviously absent image is honest; a
 * plausible substitute is not.
 *
 * Deliberately absent: **CPM**. The prototypes compute it over impressions, and
 * `SocialAccount` only reliably carries followers — `avgViews` is nullable and
 * empty for most discovered profiles. A CPM on some cards and not others, or
 * one computed over a different base than the campaign screens use, produces
 * figures that contradict each other. It comes back when there is an
 * impressions column worth dividing by.
 */
export function CreatorCard({ creator, index = 0 }: { creator: CreatorProfile; index?: number }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [portraitFailed, setPortraitFailed] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);

  const rate = rateLabel(creator, t);
  const main = primaryAccount(creator.socialAccounts);
  const portrait = creator.avatarUrl && !portraitFailed ? creator.avatarUrl : null;

  // A discovered profile has no account behind it to receive an invitation.
  const canInvite = user?.marketplaceRole === "BRAND" && creator.source === "SELF_REGISTERED";

  const ranked = [...creator.socialAccounts]
    .sort((a, b) => (b.followers ?? 0) - (a.followers ?? 0))
    .slice(0, 3);

  return (
    <article
      className={cn(
        "group relative flex aspect-4/5 animate-rise-in flex-col justify-end overflow-hidden rounded-card",
        "bg-cover bg-center transition-[transform,box-shadow] duration-200",
        "hover:-translate-y-[3px] hover:shadow-portrait-hover",
        !portrait && "adv-placeholder-portrait",
      )}
      style={{
        backgroundImage: portrait ? `url(${portrait})` : undefined,
        animationDelay: `${index * 60}ms`,
      }}
    >
      {/* Instagram's profile_picture_url is a signed CDN link that expires, so
          these will 403 eventually. Probing it with a hidden <img> lets the
          card fall back to stripes instead of showing a blank panel. */}
      {creator.avatarUrl && !portraitFailed && (
        <img
          src={creator.avatarUrl}
          alt=""
          aria-hidden
          className="hidden"
          referrerPolicy="no-referrer"
          onError={() => setPortraitFailed(true)}
        />
      )}

      {/* The whole card leads to the profile. It sits under the scrim rather
          than wrapping it so the Invite button is not a button inside a link. */}
      <Link
        // The namespaced handle stays in the URL — it is the unique key. Only
        // the rendering uses the social handle.
        to={`/creadores/${creator.handle}`}
        className="absolute inset-0 z-10"
      >
        <span className="sr-only">{creator.displayName}</span>
      </Link>

      <div className="absolute inset-x-3 top-3 z-20 flex items-start justify-end gap-2">
        <div className="flex flex-col items-end gap-[7px]">
          {canInvite && (
            <button
              type="button"
              onClick={() => setInviteOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-control bg-steel-600 px-3 py-[7px] text-fine font-semibold text-white shadow-button-on-image transition-colors hover:bg-steel-700"
            >
              <Send className="size-[13px]" strokeWidth={2.2} />
              {t("marketplace.invite.cta")}
            </button>
          )}
          <ProvenanceBadge account={main} t={t} onImage compact />
        </div>
      </div>

      {/* Text over a photograph is protected by a gradient scrim, not a
          capsule. pointer-events-none lets clicks fall through to the link. */}
      <div className="adv-scrim-portrait pointer-events-none relative z-[15] px-4 pb-4 pt-7">
        <p className="text-card-title text-white">{creator.displayName}</p>
        <p className="mt-0.5 text-micro text-on-image-muted">
          @{displayHandle(creator)}
          {creator.city ? ` · ${creator.city}` : ""}
          {!creator.city && creator.country
            ? ` · ${countryLabel(creator.country, t("marketplace.creator.unknownCountry"))}`
            : ""}
        </p>

        {(creator.niches.length > 0 || creator.acceptsGifting) && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {creator.niches.slice(0, 3).map((n) => (
              <Badge key={n} variant="onImage" className="px-2 py-[3px] text-label">
                {t(`marketplace.niches.${n}`, { defaultValue: n })}
              </Badge>
            ))}
            {creator.acceptsGifting && creator.source === "SELF_REGISTERED" && (
              <Badge variant="onImage" className="px-2 py-[3px] text-label">
                <Gift className="size-2.5" strokeWidth={2.3} />
                {t("marketplace.creator.gifting")}
              </Badge>
            )}
          </div>
        )}

        {ranked.length > 0 && (
          <div className="mt-2.5">
            {ranked.map((a) => (
              <MetricRow
                key={a.id}
                platform={PLATFORM_LABELS[a.platform]}
                followers={formatCompact(a.followers) ?? "—"}
                engagement={
                  a.engagementRate !== null ? formatPercent(a.engagementRate) : undefined
                }
                verified={a.dataSource === "OAUTH_VERIFIED"}
              />
            ))}
          </div>
        )}

        <div className="mt-2.5 flex items-end justify-between gap-2 border-t border-on-image-rule-strong pt-2.5">
          {/* Track record where the prototypes put CPM. A real count of
              finished collaborations is a better shortlisting signal than a
              rate-per-thousand we cannot compute consistently. */}
          <span className="min-w-0 flex-shrink truncate text-micro text-on-image-faint">
            {creator.avgRating !== null ? (
              <span className="inline-flex items-center gap-1">
                <Star className="size-3 fill-current" />
                {creator.avgRating.toFixed(1)}
              </span>
            ) : creator.totalCollaborations > 0 ? (
              <span className="inline-flex items-center gap-1">
                <Handshake className="size-3" />
                {creator.totalCollaborations}
              </span>
            ) : null}
          </span>

          {/* nowrap + no shrink is load-bearing: `USD 350` breaking across two
              lines clips the one number nobody can afford to misread. */}
          <p
            className={cn(
              "shrink-0 whitespace-nowrap",
              rate.known ? "text-figure-sm text-white" : "text-micro text-on-image-muted",
            )}
          >
            {rate.text}
          </p>
        </div>
      </div>

      {canInvite && (
        <InviteToCampaignDialog
          open={inviteOpen}
          onOpenChange={setInviteOpen}
          creatorProfileId={creator.id}
          creatorName={creator.displayName}
        />
      )}
    </article>
  );
}
